// Display wording for archetype (defensive) positions.
//
// The server ships `abbr` from dbo.dposn_lkup already decoded - this is a copy
// decision on top of it, not a decode. There is exactly one substitution:
//
//   COF -> LF/RF
//
// Corner outfield is ONE archetype position: the sim resolves left and right
// identically, so a player carded there plays either. "COF" is accurate but is
// jargon that appears nowhere else in the game, while LF and RF are what people
// expect to see. dposn 7 / "COF" remains the identifier everywhere behind this -
// in the lookup table, the archetype names, the proc and the sim.
//
// ⛔ Every surface that renders an archetype position goes through here. Roster,
// lineup, draft and depth-chart rows are still to come; inlining the same map in
// each of them is how one concept ends up with two names on one site.

const DISPLAY_OVERRIDES: Record<string, string> = {
  // UNSPACED on purpose. The label sits beside a pair of ratings, and a spaced
  // slash in both made "LF / RF  75RA / 85GL" read as LF=75 and RF=85. Tight, it
  // reads as one position name; the ratings beside it use a different separator
  // entirely so the two can never be mistaken for one series.
  COF: 'LF/RF',
}

/** Player-facing label for an archetype position abbreviation. */
export function archetypePositionLabel(abbr?: string | null): string {
  if (!abbr) return ''
  return DISPLAY_OVERRIDES[abbr] ?? abbr
}

/**
 * The one sentence explaining what the carded list means, shared by every surface
 * that shows it (player page, preview card, the roster pill's popover).
 *
 * Abbreviated to RA/GL: every surface this appears on has those exact column
 * headers on screen beside it, so the short form matches what the reader is
 * looking at and keeps the line to one row.
 *
 * "Unlisted" rather than "ineligible": nothing makes him ineligible - an owner can
 * field him anywhere, it is just ruinous - and a word implying prohibition would
 * undo the sentence's own point. "Off-card" is the spec's term but is jargon that
 * has to be taught first. "Unlisted" points at the chips sitting directly above it.
 *
 * Says only what is true of the WHOLE list. An earlier version read "reduced
 * slightly at these positions and heavily at any other", which is wrong about the
 * first chip: the primary carries a zero reduction by definition. The secondary
 * positions do take a small reduction, but that is visible in their own numbers
 * and does not need saying.
 *
 * The important half is the one people cannot see: nothing STOPS an owner playing
 * him anywhere, and off the card it is severe - a corner outfielder is 76 Range at
 * his own spot and 23 at shortstop. Deliberately no percentages: the off-card
 * figures come from `position_transition_lkup.oopPct`, which the spec calls
 * unvalidated tuning expected to change, and they vary by POSITION PAIR (35% to
 * first base, 70% to short) rather than being one number worth quoting.
 */
export const POSITION_REDUCTION_NOTE =
  'RA and GL reduced heavily at any unlisted position.'

/**
 * The filter CODES a player's carded positions cover, for position filters that
 * work in lineup-position labels (C, 1B, 2B, 3B, SS, LF, CF, RF).
 *
 * Raw abbreviations, NOT display labels: filters match codes, and COF expands to
 * both corners because it is one archetype position covering LF and RF. Without
 * that expansion a corner outfielder would answer to neither chip.
 */
export function cardedPositionCodes(positions?: string | null): string[] {
  if (!positions) return []
  return positions.split(',').flatMap((raw) => {
    const abbr = raw.trim()
    if (!abbr) return []
    return abbr === 'COF' ? ['LF', 'RF'] : [abbr]
  })
}

/**
 * The position label a ROW shows for a player: his own position, except that a
 * corner outfielder in an archetype league reads "LF/RF".
 *
 * The rule is deliberately narrow - only a corner outfielder's own label is ever
 * rewritten, and only when the league actually runs archetypes. Swapping the
 * column for the archetype label instead would kill the sort key and destroy the
 * scannability of a 40-row grid. Shared so the pill, the depth-chart pickers and
 * anything after them cannot drift into two answers for one player.
 */
export function rowPositionLabel(
  position?: string | null,
  eligibilityPositions?: string | null,
): string {
  const isCorner = position === 'LF' || position === 'RF'
  if (isCorner && cardedPositionCodes(eligibilityPositions).length > 0) return 'LF/RF'
  return position ?? ''
}
